import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { SEO_DESCRIPTION_MAX, SEO_TITLE_MAX, type SeoOverrideValues } from '../schemas/seo-preview';

/** The slice of the host form these fields bind to; pages and blog posts both nest it at `seo`. */
interface SeoFormSlice {
  seo: SeoOverrideValues;
}

export interface SeoOverrideFieldsProps {
  /** Only the fields, for a host that already frames them (a card). */
  bare?: boolean;
}

function Counter({ length, max }: { length: number; max: number }) {
  const { t } = useTranslation('seo');
  return (
    <span className={length > max ? 'text-destructive' : undefined}>
      {t('override.counter', { length, max })}
    </span>
  );
}

/**
 * The raw SEO override of a page or post. Empty fields fall back to the entity's own title and
 * text and to the store's defaults; the preview next to it shows the result.
 */
export function SeoOverrideFields({ bare = false }: SeoOverrideFieldsProps) {
  const { t } = useTranslation('seo');
  const form = useFormContext<SeoFormSlice>();
  const title = form.watch('seo.title');
  const description = form.watch('seo.description');

  const fields = (
    <>
      <FormField
        control={form.control}
        name="seo.title"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('override.fields.title')}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormDescription className="flex justify-between gap-2">
              <span>{t('override.fields.titleHint')}</span>
              <Counter length={title.trim().length} max={SEO_TITLE_MAX} />
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="seo.description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('override.fields.description')}</FormLabel>
            <FormControl>
              <Textarea rows={3} {...field} />
            </FormControl>
            <FormDescription className="flex justify-between gap-2">
              <span>{t('override.fields.descriptionHint')}</span>
              <Counter length={description.trim().length} max={SEO_DESCRIPTION_MAX} />
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="seo.canonical"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('override.fields.canonical')}</FormLabel>
            <FormControl>
              <Input
                type="url"
                className="font-mono text-[13px]"
                placeholder="https://"
                {...field}
              />
            </FormControl>
            <FormDescription>{t('override.fields.canonicalHint')}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="seo.noIndex"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start gap-3">
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} className="mt-0.5" />
            </FormControl>
            <div className="grid gap-1">
              <FormLabel>{t('override.fields.noIndex')}</FormLabel>
              <FormDescription>{t('override.fields.noIndexHint')}</FormDescription>
            </div>
          </FormItem>
        )}
      />
    </>
  );
  if (bare) return fields;
  return (
    <FormSection title={t('override.title')} description={t('override.description')}>
      {fields}
    </FormSection>
  );
}
